import React from 'react';
import {
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Tag,
  Link,
  Mail,
  Briefcase,
  User,
  FileText,
  MapPin,
  Phone,
  Calendar,
  Hash
} from 'lucide-react';
import { colors } from '../../styles/tokens';
import { isBaseColumn } from '../../utils/tableUtils';

/**
 * Returns the appropriate icon component for a column based on its name/type
 */
const getColumnIcon = (columnName: string, isSegment: boolean) => {
  const name = columnName.toLowerCase();

  // Segment columns
  if (isSegment) {
    return Tag;
  }

  // URL/Link columns
  if (name.includes('url') || name.includes('website') || name.includes('link')) {
    return Link;
  }

  // Email columns
  if (name.includes('email')) {
    return Mail;
  }

  // Company columns
  if (name.includes('company') || name.includes('organization')) {
    return Briefcase;
  }

  // People/Name columns
  if (name.includes('name') && !name.includes('company')) {
    return User;
  }

  // Job/Title columns
  if (name.includes('job') || name.includes('title') || name.includes('position') || name.includes('role')) {
    return User;
  }

  // Description/Bio columns
  if (name.includes('description') || name.includes('bio') || name.includes('about')) {
    return FileText;
  }

  // Location columns
  if (name.includes('location') || name.includes('city') || name.includes('country') || name.includes('address')) {
    return MapPin;
  }

  // Phone columns
  if (name.includes('phone') || name.includes('tel')) {
    return Phone;
  }

  // Date columns
  if (name.includes('date') || name.includes('founded') || name.includes('created')) {
    return Calendar;
  }

  // Number columns
  if (name.includes('size') || name.includes('count') || name.includes('number') || name.includes('revenue') || name.includes('employees')) {
    return Hash;
  }

  // Default
  return FileText;
};

export interface TableHeaderCellProps {
  column: {
    key: string;
    name: string;
    width: number;
    isSegment?: boolean;
    isSystemColumn?: boolean;
  };
  columnIndex: number;
  tableType: 'companies' | 'people' | 'custom';
  columnHasSelection: boolean;
  draggingColumnIndex: number | null;
  dragOverColumnIndex: number | null;
  editingColumn: { key: string; name: string } | null;
  editColumnName: string;
  sortDirection: 'asc' | 'desc' | null;
  // Event handlers
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnter: (e: React.DragEvent, index: number) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onResizeStart: (e: React.MouseEvent, columnKey: string) => void;
  onSort: (columnName: string) => void;
  onStartEdit: (columnKey: string, columnName: string) => void;
  onEditChange: (value: string) => void;
  onEditComplete: (columnKey: string, newName: string) => void;
  onEditCancel: () => void;
  onOpenEnrichmentMenu: (e: React.MouseEvent, columnKey: string, columnName: string) => void;
  onSelectColumn: (columnKey: string) => void;
  onHoverPreviewEnter: (e: React.MouseEvent, columnKey: string, columnName: string) => void;
  onHoverPreviewLeave: () => void;
}

/**
 * Individual table header cell with drag/drop, resize, and inline editing
 */
export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  column,
  columnIndex,
  tableType,
  columnHasSelection,
  draggingColumnIndex,
  dragOverColumnIndex,
  editingColumn,
  editColumnName,
  sortDirection,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onResizeStart,
  onSort,
  onStartEdit,
  onEditChange,
  onEditComplete,
  onEditCancel,
  onOpenEnrichmentMenu,
  onSelectColumn,
  onHoverPreviewEnter,
  onHoverPreviewLeave,
}) => {
  const isBase = isBaseColumn(column.name, tableType);
  const isEditable = !isBase && !column.isSegment && !column.isSystemColumn;
  const isFirstColumn = columnIndex === 0;

  // Build dynamic styles
  const cellStyle: React.CSSProperties = {
    width: `${column.width}px`,
    minWidth: `${column.width}px`,
    maxWidth: `${column.width}px`,
    borderRightColor: colors.neutral[200],
    cursor: draggingColumnIndex === columnIndex ? 'grabbing' : 'grab',
  };

  // First column is sticky
  if (isFirstColumn) {
    cellStyle.position = 'sticky';
    cellStyle.left = 40; // After row number column
    cellStyle.zIndex = 31;
    cellStyle.backgroundColor = columnHasSelection ? colors.primary[50] : colors.neutral[50];
    cellStyle.boxShadow = '2px 0 4px rgba(0, 0, 0, 0.03)';
  }

  // Drag over indicator
  if (dragOverColumnIndex === columnIndex && draggingColumnIndex !== columnIndex) {
    if (draggingColumnIndex !== null && draggingColumnIndex < columnIndex) {
      cellStyle.borderLeft = `2px solid ${colors.primary[400]}`;
    } else if (draggingColumnIndex !== null && draggingColumnIndex > columnIndex) {
      cellStyle.borderRight = `2px solid ${colors.primary[400]}`;
    }
    cellStyle.backgroundColor = colors.primary[50];
  }

  return (
    <div
      draggable={true}
      onDragStart={(e) => onDragStart(e, columnIndex)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, columnIndex)}
      onDragEnter={(e) => onDragEnter(e, columnIndex)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, columnIndex)}
      className="relative bg-transparent border-r border-solid last:border-r-0 transition-all duration-150 box-border group/colheader"
      style={cellStyle}
    >
      <div
        className={`flex items-center transition-colors py-2 h-10 ${
          column.isSegment ? 'px-2 justify-center text-center' : 'px-3 justify-start text-left'
        }`}
      >
        {/* Column selection checkbox - replaces content on hover */}
        <div
          className="hidden group-hover/colheader:flex absolute inset-0 items-center justify-center z-10 cursor-pointer"
          style={{ backgroundColor: columnHasSelection ? colors.primary[50] : 'rgba(250, 250, 250, 0.95)' }}
          onClick={(e) => {
            e.stopPropagation();
            onSelectColumn(column.key);
          }}
        >
          <div
            className="w-4 h-4 rounded border-2 flex items-center justify-center transition-colors"
            style={{
              borderColor: columnHasSelection ? colors.primary[500] : colors.neutral[300],
              backgroundColor: columnHasSelection ? colors.primary[500] : 'transparent'
            }}
          >
            {columnHasSelection && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>

        <div className={`group flex items-center w-full ${column.isSegment ? 'justify-center' : 'justify-between'}`}>
          <div
            className={`relative flex items-center text-[11px] font-medium text-neutral-400 ${
              column.isSegment ? 'justify-center text-center' : 'flex-1 mr-1'
            } ${isBase ? 'cursor-pointer hover:text-neutral-600' : ''} ${
              isEditable ? 'cursor-pointer hover:text-neutral-600' : ''
            }`}
            style={{
              maxWidth: column.isSegment ? `${column.width - 16}px` : `${column.width - 32}px`
            }}
            onClick={() => {
              if (isBase) {
                onSort(column.name);
              } else if (isEditable) {
                onStartEdit(column.key, column.name);
              }
            }}
            onMouseEnter={(e) => onHoverPreviewEnter(e, column.key, column.name)}
            onMouseLeave={onHoverPreviewLeave}
          >
            {editingColumn?.key === column.key ? (
              <input
                type="text"
                value={editColumnName}
                onChange={(e) => onEditChange(e.target.value)}
                onBlur={() => onEditComplete(column.key, editColumnName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onEditComplete(column.key, editColumnName);
                  } else if (e.key === 'Escape') {
                    onEditCancel();
                  }
                }}
                className="w-full px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                {/* Column type icon */}
                {(() => {
                  const Icon = getColumnIcon(column.name, column.isSegment || false);
                  return (
                    <Icon className="w-3 h-3 flex-shrink-0 text-neutral-400" />
                  );
                })()}
                <span
                  className={`min-w-0 ${column.isSegment ? 'line-clamp-2 leading-snug text-center' : 'truncate leading-none'}`}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    onHoverPreviewEnter(e, column.key, column.name);
                  }}
                >
                  {column.name}
                </span>
                {/* Sort indicator for base columns */}
                {isBase && (
                  <span className="flex-shrink-0">
                    {sortDirection === 'asc' ? (
                      <ChevronUp className="w-3 h-3 text-blue-500" />
                    ) : sortDirection === 'desc' ? (
                      <ChevronDown className="w-3 h-3 text-blue-500" />
                    ) : (
                      <ChevronUp className="w-3 h-3 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Enrichment menu button for non-base, non-segment columns */}
          {isEditable && (
            <button
              id={`column-enrichment-${column.key}-btn`}
              className="opacity-0 group-hover:opacity-100 ml-1 hover:bg-gray-200 rounded p-1 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onOpenEnrichmentMenu(e, column.key, column.name);
              }}
            >
              <MoreHorizontal className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 hover:opacity-50"
        onMouseDown={(e) => onResizeStart(e, column.key)}
      />
    </div>
  );
};

TableHeaderCell.displayName = 'TableHeaderCell';
